/**
 * Notification Service
 * Handles all notification-related API calls
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8020';
export const NOTIFICATION_READ_EVENT = 'yet2book:notifications-read';

const notifyUnreadCountChanged = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(NOTIFICATION_READ_EVENT));
};

/**
 * Get notifications for the current user
 * @param {string} token - Auth token
 * @param {boolean} unreadOnly - Only fetch unread notifications
 * @param {number} limit - Max number of notifications to fetch
 */
export const fetchNotifications = async (token, unreadOnly = false, limit = 50) => {
  const response = await fetch(
      `${API_BASE}/api/v2/notifications?unread_only=${unreadOnly}&limit=${limit}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
  );

  if (!response.ok) throw new Error('Failed to fetch notifications');
  const data = await response.json();
  return data.notifications || [];
};

export const getNotifications = async (token, unreadOnly = false, limit = 50) => {
  try {
    return await fetchNotifications(token, unreadOnly, limit);
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }
};

/**
 * Get unread notification count
 * @param {string} token - Auth token
 */
export const getUnreadCount = async (token) => {
  try {
    const response = await fetch(`${API_BASE}/api/v2/notifications/unread-count`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch unread count');
    }

    const data = await response.json();
    return data.count || 0;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }
};

/**
 * Mark a notification as read
 * @param {string} token - Auth token
 * @param {string} notificationId - Notification ID
 */
export const markAsRead = async (token, notificationId) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/v2/notifications/${notificationId}/read`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.ok) notifyUnreadCountChanged();
    return response.ok;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return false;
  }
};

/**
 * Mark all notifications as read
 * @param {string} token - Auth token
 */
export const markAllAsRead = async (token) => {
  try {
    const response = await fetch(`${API_BASE}/api/v2/notifications/mark-all-read`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) notifyUnreadCountChanged();
    return response.ok;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return false;
  }
};

/**
 * Delete a notification
 * @param {string} token - Auth token
 * @param {string} notificationId - Notification ID
 */
export const deleteNotification = async (token, notificationId) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/v2/notifications/${notificationId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    return response.ok;
  } catch (error) {
    console.error('Error deleting notification:', error);
    return false;
  }
};
